import { useParams } from '@reach/router';
import Tippy from '@tippyjs/react';
import { graphql } from 'gatsby';
import React, { FC } from 'react';

import { Card, CardBody, CardOptions, CardOptionsButton } from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import { NextIcon, PreviousIcon, RefreshIcon } from '@/components/icons';
import { useTranslate } from '@/contexts';
import { PlayerComplianceHistorySearchOrder } from '@/globalTypes';
import { assert } from '@/utils/error';
import {
  renderAmlRiskLevel,
  renderAmlStatus,
  renderCraRiskLevel,
  renderDecision,
  renderExpendingLowerBound,
  renderFundingOptions,
  renderNationality,
  renderOtherFundingDescription,
  renderPlaceOfBirth,
  renderReason,
  renderRequestedFundingOptions,
  renderSalaryLowerBound,
} from '../../hooks/sow-history/outcomeRenderers';
import useGenericSowHistory from '../../hooks/sow-history/useGenericSowHistory';

const SOWTypesTranslations: Record<string, string> = {
  SubmittedSourceOfWealthEvent: 'Submitted SOW',
  SourceOfWealthVerifiedEvent: 'Source Verified',
  FundingDocumentsRequestedEvent: 'Funding Documents Requested',
  SourceOfWealthRequiredEvent: 'SOW Required',
  AmlRiskLevelChangedEvent: 'AML Risk Level Changed',
};

export const Fragment = graphql`
  fragment SanityGlobalPlayerSowHistoryBlock on SanityGlobalPlayerSowHistoryBlock {
    title {
      ...SanityLocaleString
    }
    time {
      ...SanityLocaleString
    }
    initiator {
      ...SanityLocaleString
    }
    eventType {
      ...SanityLocaleString
    }
    outcome {
      ...SanityLocaleString
    }
  }
`;

const GlobalPlayerSowHistoryBlock: FC<{
  block: Queries.SanityGlobalPlayerSowHistoryBlockFragment;
}> = ({ block }) => {
  const params = useParams();
  const { t } = useTranslate();

  assert(params.playerGlobalId, 'missing playerGlobalId');

  const {
    history,
    fetching,
    nextPage,
    previousPage,
    refresh,
    orderBy,
    desc,
    setOrderBy,
    defaultFilter,
    updateFilter,
  } = useGenericSowHistory(params.playerGlobalId);

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'globalPlayerSowHistory',
    data: history,
    fetching,
    orderBy,
    setOrderBy,
    desc,
    defaultFilter,
    updateFilter,
    schema: (s) => [
      s.dateTimeValue({
        field: 'processedAt',
        title: t(block.time),
        fromFilterField: 'from',
        toFilterField: 'to',
        orderBy: PlayerComplianceHistorySearchOrder.processedAt,
      }),
      s.initiatorValue({
        field: 'initiator',
        title: t(block.initiator),
      }),
      s.stringValue({
        field: 'eventType',
        title: t(block.eventType),
        filterField: 'eventType',
        getValue: ({ row }) => (
          <Tippy content={row.eventType}>
            <span>{SOWTypesTranslations[row.eventType] ?? row.eventType}</span>
          </Tippy>
        ),
      }),
      s.stringValue({
        field: 'outcome',
        title: t(block.outcome),
        wrap: ({ row }) => (
          <ul>
            {renderDecision(row)}
            {renderFundingOptions(row)}
            {renderSalaryLowerBound(row)}
            {renderExpendingLowerBound(row)}
            {renderNationality(row)}
            {renderPlaceOfBirth(row)}
            {renderOtherFundingDescription(row)}
            {renderRequestedFundingOptions(row)}
            {renderAmlStatus(row)}
            {renderAmlRiskLevel(row)}
            {renderReason(row)}
            {renderCraRiskLevel(row)}
          </ul>
        ),
      }),
    ],
  });

  return (
    <Card
      size="lg"
      title={t(block.title)}
      showOptionsAtBottom
      options={
        <CardOptions>
          <CardOptionsButton
            disabled={!previousPage}
            onClick={() => previousPage && previousPage()}
          >
            <PreviousIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={!nextPage}
            onClick={() => nextPage && nextPage()}
          >
            <NextIcon />
          </CardOptionsButton>
          <GlobalCardOptions />
          <CardOptionsButton
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <DataGrid />
      </CardBody>
    </Card>
  );
};

export default GlobalPlayerSowHistoryBlock;
